import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  Typography,
  Alert,
  Stack,
  Chip,
} from "@mui/material";

const JSXGRAPH_JS = "https://cdn.jsdelivr.net/npm/jsxgraph/distrib/jsxgraphcore.js";
const JSXGRAPH_CSS = "https://cdn.jsdelivr.net/npm/jsxgraph/distrib/jsxgraph.css";

let jsxgraphLoadPromise = null;

function loadJsxGraph() {
  if (window.JXG) return Promise.resolve();
  if (jsxgraphLoadPromise) return jsxgraphLoadPromise;
  jsxgraphLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-jsxgraph="1"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = JSXGRAPH_CSS;
      link.setAttribute("data-jsxgraph", "1");
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = JSXGRAPH_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      jsxgraphLoadPromise = null;
      reject(new Error("Failed to load JSXGraph"));
    };
    document.head.appendChild(script);
  });
  return jsxgraphLoadPromise;
}

const TOOLS = {
  POINT: "point",
  SEGMENT: "segment",
  JOIN: "join",
  ANGLE: "angle",
  COMPASS: "compass",
  LABEL: "label",
  MEASURE_LEN: "measure_len",
  MEASURE_ANG: "measure_ang",
};

const HINTS = {
  [TOOLS.POINT]: "Click the board to place a point (it snaps to where arcs and lines cross). Drag a point to move it.",
  [TOOLS.SEGMENT]: "Set the length, click the start point, then click the direction.",
  [TOOLS.JOIN]: "Click two points to join them with a straight line.",
  [TOOLS.ANGLE]: "Set the angle, click the vertex, then a point on the first arm, then the side to open towards. Leave Arm cm blank and the arm runs to the edge, so two arms cross — place a point there (it snaps).",
  [TOOLS.COMPASS]: "Set the radius, then click the centre point to draw an arc.",
  [TOOLS.LABEL]: "Click one of your points to rename it (e.g. A, B, C).",
  [TOOLS.MEASURE_LEN]: "Click two points to measure the distance between them. Keep clicking points to add up a route; click the last point again to finish.",
  [TOOLS.MEASURE_ANG]: "Click a point on one arm, then the vertex, then a point on the other arm.",
};

const POINT_NAMES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const SNAP_CM = 0.3;
const PICK_CM = 0.45;

const round3 = (n) => Math.round(Number(n) * 1000) / 1000;
const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));

function nextPointName(existing) {
  const used = new Set(existing.map((n) => String(n).toUpperCase()));
  for (const ch of POINT_NAMES) {
    if (!used.has(ch)) return ch;
  }
  let i = 1;
  while (used.has(`P${i}`)) i += 1;
  return `P${i}`;
}

function angleDegAt(a, v, b) {
  const vax = a[0] - v[0];
  const vay = a[1] - v[1];
  const vbx = b[0] - v[0];
  const vby = b[1] - v[1];
  const den = Math.hypot(vax, vay) * Math.hypot(vbx, vby);
  if (den < 1e-9) return null;
  return (Math.acos(Math.max(-1, Math.min(1, (vax * vbx + vay * vby) / den))) * 180) / Math.PI;
}

function circleCircle(c1, r1, c2, r2) {
  const d = Math.hypot(c2[0] - c1[0], c2[1] - c1[1]);
  if (d < 1e-9 || d > r1 + r2 + 1e-9 || d < Math.abs(r1 - r2) - 1e-9) return [];
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
  const mx = c1[0] + (a * (c2[0] - c1[0])) / d;
  const my = c1[1] + (a * (c2[1] - c1[1])) / d;
  const ox = (h * (c2[1] - c1[1])) / d;
  const oy = (h * (c2[0] - c1[0])) / d;
  return [[mx + ox, my - oy], [mx - ox, my + oy]];
}

function circleSegment(c, r, p, q) {
  const dx = q[0] - p[0];
  const dy = q[1] - p[1];
  const fx = p[0] - c[0];
  const fy = p[1] - c[1];
  const A = dx * dx + dy * dy;
  if (A < 1e-12) return [];
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - r * r;
  const disc = B * B - 4 * A * C;
  if (disc < 0) return [];
  const s = Math.sqrt(disc);
  return [(-B - s) / (2 * A), (-B + s) / (2 * A)]
    .filter((t) => t >= -1e-6 && t <= 1 + 1e-6)
    .map((t) => [p[0] + t * dx, p[1] + t * dy]);
}

function segmentSegment(p, q, r, s) {
  const d = (q[0] - p[0]) * (s[1] - r[1]) - (q[1] - p[1]) * (s[0] - r[0]);
  if (Math.abs(d) < 1e-12) return [];
  const t = ((r[0] - p[0]) * (s[1] - r[1]) - (r[1] - p[1]) * (s[0] - r[0])) / d;
  const u = ((r[0] - p[0]) * (q[1] - p[1]) - (r[1] - p[1]) * (q[0] - p[0])) / d;
  if (t < -1e-6 || t > 1 + 1e-6 || u < -1e-6 || u > 1 + 1e-6) return [];
  return [[p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])]];
}

const asXY = (p) =>
  Array.isArray(p) && p.length >= 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1]))
    ? [Number(p[0]), Number(p[1])]
    : null;

/**
 * Calibrated construction board: 1 board unit = 1 cm (independent of screen CSS pixels).
 *
 * Two uses:
 *  - Scratch (`config` only): rough work beside a typed answer. Nothing is submitted.
 *  - Graded answer (`value` / `onChange`): the `construction` interactive kind. What the student builds
 *    is serialised as { points: [[x, y, name]], segments: [[nameA, nameB]], circles: [[centre, r]],
 *    measures: { A: "3.4" } } and graded server-side by gradeInteractiveConstruction.
 *
 * `given` pre-draws fixed context (the printed point A, a base line) that the student cannot move.
 * `measures` asks for typed readings ("the length marked A", in cm) under the board.
 * `backgroundImage` ({ url, widthCm } or { url, pixelsPerCm } against its natural size) lays a scale drawing (a map) under the grid at its true size, so
 * the student can mark points on it and measure a route piece by piece with Measure cm.
 */
export default function ConstructGeometryBoard({
  config,
  value = null,
  onChange = null,
  readOnly = false,
  given: givenProp = null,
  measures = null,
  extent: extentProp = null,
  showIntro = true,
  title = "Construction board (1 unit = 1 cm)",
  height = null,
  backgroundImage = null,
}) {
  const boardId = `construct-${useId().replace(/:/g, "")}`;
  const boardRef = useRef(null);
  const pointsRef = useRef([]); // { el, name, given }
  const segmentsRef = useRef([]); // { el, a, b, given }
  const givenLinesRef = useRef([]); // directions of printed coordinate lines (for snapping)
  const circlesRef = useRef([]); // { el, c, r }
  const undoRef = useRef([]); // stack of { type, item }
  const pickRef = useRef([]);
  const pathRef = useRef(null); // Measure cm route: { names: [...], total }
  const toolRef = useRef(TOOLS.POINT);
  const lengthRef = useRef(3.5);
  // The Angle tool's own arm length. Blank = the arm runs to the edge of the board, like a ruler line:
  // a triangle from two angles ("45° at A, 90° at B, C where the arms meet", ISEB 2016 Q14) needs the
  // arms to cross, and nobody knows how long they have to be until they do.
  const armRef = useRef(null);
  // Angle arms drawn to the board edge: pencil guide lines (no end point, not part of the answer) that
  // a point snaps onto where they cross.
  const guidesRef = useRef([]);
  const angleRef = useRef(90);
  const radiusRef = useRef(4);
  const measuresRef = useRef({});
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);

  const [tool, setTool] = useState(TOOLS.POINT);
  const [lengthCm, setLengthCm] = useState(3.5);
  const [armCmInput, setArmCmInput] = useState("");
  const [angleDeg, setAngleDeg] = useState(90);
  const [radiusCm, setRadiusCm] = useState(4);
  const [hint, setHint] = useState("Loading board…");
  const [readout, setReadout] = useState(null);
  const [narrow, setNarrow] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [renaming, setRenaming] = useState(null); // { item, text, error }
  const [measureValues, setMeasureValues] = useState(() => {
    const m = value && typeof value === "object" && !Array.isArray(value) ? value.measures : null;
    return m && typeof m === "object" ? { ...m } : {};
  });

  toolRef.current = tool;
  lengthRef.current = lengthCm;
  armRef.current = armCmInput === "" ? null : Number(armCmInput);
  angleRef.current = angleDeg;
  radiusRef.current = radiusCm;
  measuresRef.current = measureValues;
  onChangeRef.current = onChange;

  const graded = typeof onChange === "function" || readOnly;
  const extent = Math.max(6, Math.min(30, Number(extentProp) || Number(config?.board_extent_cm) || 12));
  const givenPoints = Array.isArray(givenProp?.points) ? givenProp.points : [];
  const givenSegments = Array.isArray(givenProp?.segments) ? givenProp.segments : [];
  const givenKey = JSON.stringify([givenPoints, givenSegments]);
  const chipsGiven = Array.isArray(config?.given) ? config.given : [];
  const unknowns = Array.isArray(config?.unknowns) ? config.unknowns : [];
  const measureList = Array.isArray(measures) ? measures.filter((m) => m && String(m.label || "").trim()) : [];

  const serialize = useCallback(() => {
    const own = pointsRef.current.filter((p) => !p.given);
    const out = {
      points: own.map((p) => [round3(p.el.X()), round3(p.el.Y()), p.name]),
      segments: segmentsRef.current.filter((s) => !s.given).map((s) => [s.a.name, s.b.name]),
      circles: circlesRef.current.map((c) => [c.c.name, round3(c.r)]),
    };
    const m = measuresRef.current || {};
    if (Object.keys(m).length) out.measures = { ...m };
    return out;
  }, []);

  const emit = useCallback(() => {
    if (typeof onChangeRef.current === "function") onChangeRef.current(serialize());
  }, [serialize]);

  const findNearestPoint = useCallback((x, y, maxDist = PICK_CM) => {
    let best = null;
    let bestD = maxDist;
    for (const p of pointsRef.current) {
      const d = Math.hypot(p.el.X() - x, p.el.Y() - y);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  }, []);

  // Where drawn arcs and lines cross — the points a compass construction is built from.
  const snapTarget = useCallback((x, y) => {
    const circles = circlesRef.current.map((c) => ({ c: [c.c.el.X(), c.c.el.Y()], r: c.r }));
    const segs = segmentsRef.current.map((s) => [[s.a.el.X(), s.a.el.Y()], [s.b.el.X(), s.b.el.Y()]]);
    for (const g of guidesRef.current) segs.push([g.a, g.b]);
    for (const g of givenSegments) {
      const a = asXY(g?.[0]);
      const b = asXY(g?.[1]);
      if (a && b) segs.push([a, b]);
    }
    const cands = [];
    for (let i = 0; i < circles.length; i += 1) {
      for (let j = i + 1; j < circles.length; j += 1) {
        cands.push(...circleCircle(circles[i].c, circles[i].r, circles[j].c, circles[j].r));
      }
      for (const s of segs) cands.push(...circleSegment(circles[i].c, circles[i].r, s[0], s[1]));
    }
    for (let i = 0; i < segs.length; i += 1) {
      for (let j = i + 1; j < segs.length; j += 1) {
        cands.push(...segmentSegment(segs[i][0], segs[i][1], segs[j][0], segs[j][1]));
      }
    }
    let best = null;
    let bestD = SNAP_CM;
    for (const c of cands) {
      const d = Math.hypot(c[0] - x, c[1] - y);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best;
  }, [givenKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const makePoint = useCallback((board, x, y, name, { given = false } = {}) => {
    const label = name || nextPointName(pointsRef.current.map((p) => p.name));
    const el = board.create("point", [x, y], {
      name: label,
      size: given ? 2.5 : 3,
      face: given ? "x" : "o",
      withLabel: true,
      label: { offset: [8, 8], fontSize: 13 },
      color: given ? "#111827" : "#1565c0",
      highlightColor: given ? "#111827" : "#0d47a1",
      fixed: given || readOnly || toolRef.current !== TOOLS.POINT,
      showInfobox: false,
    });
    const item = { el, name: label, given };
    pointsRef.current.push(item);
    return item;
  }, [readOnly]);

  const makeSegment = useCallback((board, a, b, { given = false } = {}) => {
    const existing = segmentsRef.current.find(
      (s) => (s.a === a && s.b === b) || (s.a === b && s.b === a)
    );
    if (existing) return existing;
    const el = board.create("segment", [a.el, b.el], {
      strokeColor: given ? "#111827" : "#2e7d32",
      strokeWidth: 2,
      fixed: true,
      highlight: false,
    });
    const item = { el, a, b, given };
    segmentsRef.current.push(item);
    return item;
  }, []);

  const makeCircle = useCallback((board, c, r) => {
    const el = board.create("circle", [c.el, r], {
      strokeColor: "#9333ea",
      strokeWidth: 1,
      dash: 2,
      fillColor: "none",
      fillOpacity: 0,
      fixed: true,
      highlight: false,
      hasInnerPoints: false,
    });
    const item = { el, c, r };
    circlesRef.current.push(item);
    return item;
  }, []);

  const newPointAt = useCallback((board, x, y) => {
    const snap = snapTarget(x, y);
    const [px, py] = snap || [x, y];
    const item = makePoint(board, px, py);
    undoRef.current.push({ type: "point", item });
    return item;
  }, [makePoint, snapTarget]);

  const pointOrNew = useCallback((board, x, y) => {
    const near = findNearestPoint(x, y);
    return near || newPointAt(board, x, y);
  }, [findNearestPoint, newPointAt]);

  const handleBoardDown = useCallback((e) => {
    const board = boardRef.current;
    if (!board || !window.JXG || readOnly) return;
    let x;
    let y;
    try {
      if (typeof board.getUsrCoordsOfMouse === "function") {
        const c = board.getUsrCoordsOfMouse(e);
        x = c[0];
        y = c[1];
      } else {
        const coords = new window.JXG.Coords(window.JXG.COORDS_BY_SCREEN, board.getMousePosition(e), board);
        x = coords.usrCoords[1];
        y = coords.usrCoords[2];
      }
    } catch {
      return;
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const mode = toolRef.current;

    if (mode === TOOLS.POINT) {
      if (findNearestPoint(x, y, 0.35)) {
        setHint("Drag the point to move it, or click empty space for a new point.");
        return;
      }
      const p = newPointAt(board, x, y);
      setHint(`Point ${p.name} placed. Use Label to rename it.`);
      setReadout(null);
      emit();
      return;
    }

    if (mode === TOOLS.LABEL) {
      const near = findNearestPoint(x, y);
      if (!near) {
        setHint("Click near one of your points.");
        return;
      }
      if (near.given) {
        setHint(`${near.name} is printed on the question and cannot be renamed.`);
        return;
      }
      setRenaming({ item: near, text: near.name, error: null });
      return;
    }

    if (mode === TOOLS.SEGMENT) {
      const len = Number(lengthRef.current);
      if (!Number.isFinite(len) || len <= 0) {
        setHint("Enter a positive length in cm first.");
        return;
      }
      if (pickRef.current.length === 0) {
        const start = pointOrNew(board, x, y);
        pickRef.current = [start];
        setHint(`Start at ${start.name}. Click the direction for a ${len} cm line.`);
        emit();
        return;
      }
      const start = pickRef.current[0];
      const sx = start.el.X();
      const sy = start.el.Y();
      let dx = x - sx;
      let dy = y - sy;
      const d = Math.hypot(dx, dy);
      if (d < 1e-6) {
        setHint("Click away from the start point to set the direction.");
        return;
      }
      // "Draw AB 5.5 cm along the line": a click a hair off the printed line put B below it and failed
      // the "along the line" check (ISEB 2016 Q14). Within 8° of a given line's direction, follow it.
      let ux = dx / d;
      let uy = dy / d;
      const givenDirs = [
        ...segmentsRef.current.filter((s) => s.given).map((s) => [s.b.el.X() - s.a.el.X(), s.b.el.Y() - s.a.el.Y()]),
        ...givenLinesRef.current,
      ];
      for (const [gx, gy] of givenDirs) {
        const gl = Math.hypot(gx, gy);
        if (gl < 1e-6) continue;
        const dot = (ux * gx + uy * gy) / gl;
        if (Math.abs(dot) >= Math.cos((8 * Math.PI) / 180)) {
          const sign = dot < 0 ? -1 : 1;
          ux = (sign * gx) / gl;
          uy = (sign * gy) / gl;
          break;
        }
      }
      dx = ux * len;
      dy = uy * len;
      const tx = sx + dx;
      const ty = sy + dy;
      const nearEnd = findNearestPoint(tx, ty, 0.2);
      const end = nearEnd && nearEnd !== start ? nearEnd : makePoint(board, tx, ty);
      if (end !== nearEnd) undoRef.current.push({ type: "point", item: end });
      const seg = makeSegment(board, start, end);
      undoRef.current.push({ type: "segment", item: seg });
      pickRef.current = [];
      setHint(`Drew ${len} cm line ${start.name}${end.name}.`);
      setReadout({ kind: "length", label: `${start.name}${end.name}`, value: len, unit: "cm" });
      emit();
      return;
    }

    if (mode === TOOLS.JOIN) {
      const near = findNearestPoint(x, y);
      if (!near) {
        setHint("Click on an existing point (place it first with Point if needed).");
        return;
      }
      if (pickRef.current.length === 0) {
        pickRef.current = [near];
        setHint(`From ${near.name}. Click the point to join it to.`);
        return;
      }
      const a = pickRef.current[0];
      if (a === near) {
        setHint("Pick a different point.");
        return;
      }
      const seg = makeSegment(board, a, near);
      undoRef.current.push({ type: "segment", item: seg });
      pickRef.current = [];
      const len = Math.hypot(a.el.X() - near.el.X(), a.el.Y() - near.el.Y());
      setHint(`Joined ${a.name}${near.name}.`);
      setReadout({ kind: "length", label: `${a.name}${near.name}`, value: len, unit: "cm" });
      emit();
      return;
    }

    if (mode === TOOLS.COMPASS) {
      const r = Number(radiusRef.current);
      if (!Number.isFinite(r) || r <= 0) {
        setHint("Enter a positive radius in cm first.");
        return;
      }
      const centre = pointOrNew(board, x, y);
      const c = makeCircle(board, centre, r);
      undoRef.current.push({ type: "circle", item: c });
      setHint(`Arc of radius ${r} cm about ${centre.name}. Place a point where two arcs cross — it snaps there.`);
      emit();
      return;
    }

    if (mode === TOOLS.ANGLE) {
      const deg = Number(angleRef.current);
      if (!Number.isFinite(deg) || deg <= 0 || deg >= 360) {
        setHint("Enter an angle between 0 and 360 degrees.");
        return;
      }
      if (pickRef.current.length === 0) {
        const vertex = pointOrNew(board, x, y);
        pickRef.current = [vertex];
        setHint(`Vertex ${vertex.name}. Click a point on the first arm.`);
        emit();
        return;
      }
      if (pickRef.current.length === 1) {
        const rayPt = pointOrNew(board, x, y);
        if (rayPt === pickRef.current[0]) {
          setHint("Pick a different point for the first arm.");
          return;
        }
        pickRef.current = [pickRef.current[0], rayPt];
        setHint(`First arm ${pickRef.current[0].name}${rayPt.name}. Click the side to open ${deg}° towards.`);
        emit();
        return;
      }
      const [vertex, rayPt] = pickRef.current;
      const vx = vertex.el.X();
      const vy = vertex.el.Y();
      const rx = rayPt.el.X() - vx;
      const ry = rayPt.el.Y() - vy;
      const rLen = Math.hypot(rx, ry) || 1;
      const baseAng = Math.atan2(ry, rx);
      const side = rx * (y - vy) - ry * (x - vx);
      const signed = side >= 0 ? 1 : -1;
      const newAng = baseAng + (signed * deg * Math.PI) / 180;
      const armCm = armRef.current;
      const dirX = Math.cos(newAng);
      const dirY = Math.sin(newAng);
      // Distance from the vertex to the board edge along the new arm.
      const toEdge = Math.min(
        dirX > 1e-9 ? (extent - vx) / dirX : dirX < -1e-9 ? (0 - vx) / dirX : Infinity,
        dirY > 1e-9 ? (extent - vy) / dirY : dirY < -1e-9 ? (0 - vy) / dirY : Infinity,
      );
      const toTheEdge = !(Number.isFinite(armCm) && armCm > 0) && Number.isFinite(toEdge) && toEdge > 0.5;
      const outLen = Number.isFinite(armCm) && armCm > 0
        ? armCm
        : (toTheEdge ? toEdge : Math.max(rLen, 3));
      const s1 = makeSegment(board, vertex, rayPt);
      undoRef.current.push({ type: "segment", item: s1 });
      if (toTheEdge) {
        const a = [vx, vy];
        const b = [vx + dirX * outLen, vy + dirY * outLen];
        const el = board.create("segment", [a, b], {
          strokeColor: "#64748b", strokeWidth: 1.5, dash: 2, fixed: true, highlight: false,
          point1: { visible: false }, point2: { visible: false },
        });
        const guide = { el, a, b };
        guidesRef.current.push(guide);
        undoRef.current.push({ type: "guide", item: guide });
        pickRef.current = [];
        setHint(`Drew ${deg}° at ${vertex.name}. Draw the other angle, then place a point where the arms cross — it snaps there.`);
        setReadout({ kind: "angle", label: `∠ at ${vertex.name}`, value: deg, unit: "°" });
        emit();
        return;
      }
      const end = makePoint(board, vx + dirX * outLen, vy + dirY * outLen);
      undoRef.current.push({ type: "point", item: end });
      const s2 = makeSegment(board, vertex, end);
      undoRef.current.push({ type: "segment", item: s2 });
      pickRef.current = [];
      setHint(`Drew ${deg}° at ${vertex.name} with a ${outLen.toFixed(1)} cm arm ending at ${end.name}.`);
      setReadout({ kind: "angle", label: `∠${rayPt.name}${vertex.name}${end.name}`, value: deg, unit: "°" });
      emit();
      return;
    }

    if (mode === TOOLS.MEASURE_LEN) {
      const near = findNearestPoint(x, y);
      if (!near) {
        setHint("Click near an existing point.");
        return;
      }
      if (pickRef.current.length === 0) {
        pickRef.current = [near];
        pathRef.current = { names: [near.name], total: 0 };
        setHint(`From ${near.name}. Click the other end.`);
        return;
      }
      const a = pickRef.current[0];
      if (a === near) {
        // Clicking the last point again finishes the route; the total stays on screen.
        const hadLegs = (pathRef.current?.names?.length || 0) > 1;
        pickRef.current = [];
        pathRef.current = null;
        setHint(hadLegs ? "Route finished. Click a point to start a new measurement." : "Pick two different points.");
        return;
      }
      const cm = Math.hypot(a.el.X() - near.el.X(), a.el.Y() - near.el.Y());
      // "Measure along the route": each further click adds the next leg, so a bent path (the map in
      // City of London 2008 Q12) is the sum of its straight pieces, as the student would do with a ruler.
      const path = pathRef.current || { names: [a.name], total: 0 };
      path.names.push(near.name);
      path.total += cm;
      pathRef.current = path;
      pickRef.current = [near];
      const legs = path.names.length - 1;
      setReadout({
        kind: "length",
        label: legs > 1 ? `Route ${path.names.join("")}` : `${a.name}${near.name}`,
        value: path.total,
        unit: "cm",
      });
      setHint(legs > 1
        ? `${a.name}${near.name} ≈ ${cm.toFixed(1)} cm — route so far ${path.total.toFixed(1)} cm. Click the next point, or ${near.name} again to finish.`
        : `${a.name}${near.name} ≈ ${cm.toFixed(1)} cm. Click another point to carry on along a route, or ${near.name} again to finish.`);
      return;
    }

    if (mode === TOOLS.MEASURE_ANG) {
      const near = findNearestPoint(x, y);
      if (!near) {
        setHint("Click near an existing point.");
        return;
      }
      if (pickRef.current.length < 2) {
        pickRef.current = [...pickRef.current, near];
        setHint(pickRef.current.length === 1 ? `Arm point ${near.name}. Now click the vertex.` : `Vertex ${near.name}. Now click a point on the other arm.`);
        return;
      }
      const [pA, pV] = pickRef.current;
      const deg = angleDegAt([pA.el.X(), pA.el.Y()], [pV.el.X(), pV.el.Y()], [near.el.X(), near.el.Y()]);
      pickRef.current = [];
      if (deg == null) {
        setHint("Could not measure that angle.");
        return;
      }
      setReadout({ kind: "angle", label: `∠${pA.name}${pV.name}${near.name}`, value: deg, unit: "°" });
      setHint(`∠${pA.name}${pV.name}${near.name} ≈ ${deg.toFixed(0)}°.`);
    }
  }, [emit, findNearestPoint, makeCircle, makePoint, makeSegment, newPointAt, pointOrNew, readOnly]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 700px)");
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setReady(false);

    loadJsxGraph()
      .then(() => {
        if (cancelled || !window.JXG) return;
        const board = window.JXG.JSXGraph.initBoard(boardId, {
          boundingbox: [-0.5, extent + 0.5, extent + 0.5, -0.5],
          axis: true,
          grid: true,
          showCopyright: false,
          showNavigation: true,
          keepaspectratio: true,
          resize: { enabled: true, throttle: 200 },
          pan: { enabled: true, needTwoFingers: true },
          zoom: { enabled: true, wheel: true, needShift: true, pinch: true },
        });
        boardRef.current = board;
        pointsRef.current = [];
        segmentsRef.current = [];
        givenLinesRef.current = [];
        circlesRef.current = [];
        guidesRef.current = [];
        undoRef.current = [];
        pickRef.current = [];

        board.suspendUpdate();
        // Printed context first, so the student's own points are named around it.
        for (const g of givenPoints) {
          const xy = asXY(g);
          if (!xy) continue;
          makePoint(board, xy[0], xy[1], String(g[2] || "").trim() || undefined, { given: true });
        }
        for (const g of givenSegments) {
          if (Array.isArray(g) && typeof g[0] === "string") {
            const a = pointsRef.current.find((p) => p.name === g[0]);
            const b = pointsRef.current.find((p) => p.name === g[1]);
            if (a && b) makeSegment(board, a, b, { given: true });
            continue;
          }
          const a = asXY(g?.[0]);
          const b = asXY(g?.[1]);
          if (!a || !b) continue;
          givenLinesRef.current.push([b[0] - a[0], b[1] - a[1]]);
          board.create("segment", [a, b], {
            strokeColor: "#111827",
            strokeWidth: 2,
            dash: g?.[2] === "dashed" ? 2 : 0,
            fixed: true,
            highlight: false,
            point1: { visible: false },
            point2: { visible: false },
          });
        }
        // Restore the student's work (or the model answer in a read-only reveal).
        const v = initialValueRef.current;
        if (v && typeof v === "object" && !Array.isArray(v)) {
          for (const p of Array.isArray(v.points) ? v.points : []) {
            const xy = asXY(p);
            if (!xy) continue;
            const name = String(p[2] || "").trim();
            if (name && pointsRef.current.some((q) => q.name === name)) continue;
            const item = makePoint(board, xy[0], xy[1], name || undefined);
            undoRef.current.push({ type: "point", item });
          }
          const byName = (n) => pointsRef.current.find((p) => p.name === n);
          for (const c of Array.isArray(v.circles) ? v.circles : []) {
            const centre = byName(c?.[0]);
            const r = num(c?.[1]);
            if (centre && Number.isFinite(r) && r > 0) undoRef.current.push({ type: "circle", item: makeCircle(board, centre, r) });
          }
          for (const s of Array.isArray(v.segments) ? v.segments : []) {
            const a = byName(s?.[0]);
            const b = byName(s?.[1]);
            if (a && b && a !== b) undoRef.current.push({ type: "segment", item: makeSegment(board, a, b) });
          }
        }
        board.unsuspendUpdate();

        // The scale drawing goes underneath at its true size (bottom-left corner on the origin), and the
        // view widens to show all of it.
        const bgUrl = backgroundImage?.url;
        if (bgUrl && (Number(backgroundImage?.widthCm) > 0 || Number(backgroundImage?.pixelsPerCm) > 0)) {
          const img = new Image();
          img.onload = () => {
            if (cancelled || boardRef.current !== board || !img.naturalWidth) return;
            const bgWidth = Number(backgroundImage.widthCm) > 0
              ? Number(backgroundImage.widthCm)
              : img.naturalWidth / Number(backgroundImage.pixelsPerCm);
            const h = bgWidth * (img.naturalHeight / img.naturalWidth);
            board.create("image", [bgUrl, [0, 0], [bgWidth, h]], { fixed: true, highlight: false, layer: 0 });
            const span = Math.max(extent, bgWidth, h);
            board.setBoundingBox([-0.5, span + 0.5, span + 0.5, -0.5], true);
          };
          img.src = bgUrl;
        }

        if (!readOnly) {
          board.on("down", handleBoardDown);
          // A dragged point changes every length and angle built on it — report the new positions.
          board.on("up", () => emit());
        }
        setReady(true);
        setHint(readOnly ? "" : HINTS[TOOLS.POINT]);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the construction board. Check your connection and retry.");
      });

    return () => {
      cancelled = true;
      if (boardRef.current) {
        try {
          window.JXG.JSXGraph.freeBoard(boardRef.current);
        } catch {
          /* ignore */
        }
        boardRef.current = null;
      }
      pointsRef.current = [];
      segmentsRef.current = [];
      givenLinesRef.current = [];
      circlesRef.current = [];
      guidesRef.current = [];
      undoRef.current = [];
    };
  }, [boardId, extent, givenKey, readOnly, backgroundImage?.url, backgroundImage?.widthCm, backgroundImage?.pixelsPerCm]); // eslint-disable-line react-hooks/exhaustive-deps

  // A shared board stays mounted while the student switches parts. The drawing stays; the reading
  // boxes belong to the part that is now active, so they follow the value the page hands in.
  const valueMeasuresKey = JSON.stringify(
    value && typeof value === "object" && !Array.isArray(value) && value.measures && typeof value.measures === "object"
      ? value.measures
      : {}
  );
  useEffect(() => {
    if (!ready || readOnly) return;
    let incoming = {};
    try { incoming = JSON.parse(valueMeasuresKey); } catch { incoming = {}; }
    setMeasureValues((prev) => {
      if (JSON.stringify(prev || {}) === valueMeasuresKey) return prev;
      measuresRef.current = incoming;
      return incoming;
    });
  }, [ready, readOnly, valueMeasuresKey]);

  // Points are only draggable with the Point tool, so a click with Join or Compass cannot nudge one.
  useEffect(() => {
    if (!ready) return;
    for (const p of pointsRef.current) {
      if (p.given) continue;
      try {
        p.el.setAttribute({ fixed: readOnly || tool !== TOOLS.POINT });
      } catch {
        /* ignore */
      }
    }
  }, [tool, ready, readOnly]);

  const removeItem = (board, entry) => {
    const { type, item } = entry;
    if (type === "point") {
      // Anything hanging off the point goes with it.
      for (const s of segmentsRef.current.filter((s) => s.a === item || s.b === item)) {
        try { board.removeObject(s.el); } catch { /* ignore */ }
      }
      for (const c of circlesRef.current.filter((c) => c.c === item)) {
        try { board.removeObject(c.el); } catch { /* ignore */ }
      }
      segmentsRef.current = segmentsRef.current.filter((s) => s.a !== item && s.b !== item);
      circlesRef.current = circlesRef.current.filter((c) => c.c !== item);
      pointsRef.current = pointsRef.current.filter((p) => p !== item);
    } else if (type === "segment") {
      segmentsRef.current = segmentsRef.current.filter((s) => s !== item);
    } else if (type === "circle") {
      circlesRef.current = circlesRef.current.filter((c) => c !== item);
    } else if (type === "guide") {
      guidesRef.current = guidesRef.current.filter((g) => g !== item);
    }
    try { board.removeObject(item.el); } catch { /* ignore */ }
  };

  const handleUndo = () => {
    const board = boardRef.current;
    if (!board) return;
    let entry = undoRef.current.pop();
    // Skip entries already removed along with their point.
    while (entry && entry.type !== "point" && ![...segmentsRef.current, ...circlesRef.current, ...guidesRef.current].includes(entry.item)) {
      entry = undoRef.current.pop();
    }
    if (!entry) return;
    removeItem(board, entry);
    pickRef.current = [];
    setHint("Undid the last step.");
    setReadout(null);
    emit();
  };

  const handleClear = () => {
    const board = boardRef.current;
    if (!board) return;
    for (const entry of [...undoRef.current].reverse()) {
      if (entry.type === "point" && !pointsRef.current.includes(entry.item)) continue;
      removeItem(board, entry);
    }
    undoRef.current = [];
    pickRef.current = [];
    pathRef.current = null;
    setReadout(null);
    setHint("Board cleared. Start again.");
    emit();
  };

  const selectTool = (next) => {
    setTool(next);
    pickRef.current = [];
    pathRef.current = null;
    setHint(HINTS[next]);
  };

  const commitRename = () => {
    if (!renaming) return;
    const name = String(renaming.text || "").trim().toUpperCase().slice(0, 3);
    if (!name || !/^[A-Z][A-Z0-9']{0,2}$/.test(name)) {
      setRenaming({ ...renaming, error: "Use a capital letter, e.g. C" });
      return;
    }
    if (pointsRef.current.some((p) => p !== renaming.item && p.name === name)) {
      setRenaming({ ...renaming, error: `${name} is already used` });
      return;
    }
    const { item } = renaming;
    item.name = name;
    try {
      if (typeof item.el.setName === "function") item.el.setName(name);
      else {
        item.el.name = name;
        item.el.label?.setText?.(name);
      }
      boardRef.current?.update();
    } catch {
      /* ignore */
    }
    setRenaming(null);
    setHint(`Renamed to ${name}.`);
    emit();
  };

  const setMeasure = (label, v) => {
    const next = { ...measuresRef.current, [label]: v };
    if (v === "" || v === null) delete next[label];
    measuresRef.current = next;
    setMeasureValues(next);
    emit();
  };

  const toolButton = (key, label) => (
    <Button key={key} variant={tool === key ? "contained" : "outlined"} onClick={() => selectTool(key)}>
      {label}
    </Button>
  );

  return (
    <Box sx={{ mb: 2 }}>
      {title ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
      ) : null}
      {showIntro && !readOnly ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {graded
            ? "Construct your answer here — this board is what gets marked. Any diagram above is not to scale. Use Compass for arcs, then place points where the arcs cross."
            : "The rough diagram above is for reference only — it is not to scale. Construct accurately here, then type measured answers in the answer box."}
        </Typography>
      ) : null}

      {narrow && !readOnly ? (
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
          Tip: rotate to landscape or use a tablet/desktop for easier construction.
        </Alert>
      ) : null}

      {(chipsGiven.length > 0 || unknowns.length > 0) && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {chipsGiven.map((g, i) => (
            <Chip
              key={`g-${i}`}
              size="small"
              label={
                g.type === "angle"
                  ? `Given: ${g.label || "angle"} ${g.deg}°`
                  : `Given: ${g.label || "side"} ${g.cm != null ? `${g.cm} cm` : ""}`
              }
              variant="outlined"
            />
          ))}
          {unknowns.map((u, i) => (
            <Chip key={`u-${i}`} size="small" color="primary" label={`Find: ${u}`} variant="outlined" />
          ))}
        </Stack>
      )}

      {!readOnly ? (
        <Stack spacing={1} sx={{ mb: 1 }}>
          <ButtonGroup size="small" variant="outlined" sx={{ flexWrap: "wrap" }}>
            {toolButton(TOOLS.POINT, "Point / move")}
            {toolButton(TOOLS.SEGMENT, "Line of length")}
            {toolButton(TOOLS.JOIN, "Join")}
            {toolButton(TOOLS.COMPASS, "Compass")}
            {toolButton(TOOLS.ANGLE, "Angle")}
            {toolButton(TOOLS.LABEL, "Label")}
            {toolButton(TOOLS.MEASURE_LEN, "Measure cm")}
            {toolButton(TOOLS.MEASURE_ANG, "Measure °")}
          </ButtonGroup>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {tool === TOOLS.SEGMENT ? (
              <TextField size="small" label="Length cm" type="number" value={lengthCm}
                onChange={(e) => setLengthCm(parseFloat(e.target.value))}
                inputProps={{ step: 0.1, min: 0.1, style: { width: 70 } }} />
            ) : null}
            {tool === TOOLS.ANGLE ? (
              <TextField size="small" label="Arm cm" type="number" value={armCmInput} placeholder="to edge"
                onChange={(e) => setArmCmInput(e.target.value)}
                helperText={armCmInput === "" ? "Blank: arm runs to the edge" : undefined}
                inputProps={{ step: 0.1, min: 0.1, style: { width: 70 } }} />
            ) : null}
            {tool === TOOLS.COMPASS ? (
              <TextField size="small" label="Radius cm" type="number" value={radiusCm}
                onChange={(e) => setRadiusCm(parseFloat(e.target.value))}
                inputProps={{ step: 0.1, min: 0.1, style: { width: 70 } }} />
            ) : null}
            {tool === TOOLS.ANGLE ? (
              <TextField size="small" label="Angle °" type="number" value={angleDeg}
                onChange={(e) => setAngleDeg(parseFloat(e.target.value))}
                inputProps={{ step: 1, min: 1, max: 359, style: { width: 64 } }} />
            ) : null}
            <Button size="small" onClick={handleUndo} disabled={!ready}>Undo</Button>
            <Button size="small" color="warning" onClick={handleClear} disabled={!ready}>Clear</Button>
          </Stack>
          {/* Fixed-height status line. Anything that appears or disappears ABOVE the board moves it,
              and JSXGraph keeps the old screen position — the next click then lands up to 2 cm away. */}
          <Box sx={{ minHeight: 44, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography variant="caption" color="primary.main" sx={{ display: "block", fontWeight: 600, lineHeight: 1.35, overflow: "hidden", maxHeight: 22 }}>
              {hint || "\u00a0"}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "success.dark", lineHeight: 1.35, minHeight: 18 }}>
              {readout
                ? readout.kind === "length"
                  ? `${readout.label} = ${Number(readout.value).toFixed(1)} ${readout.unit}`
                  : `${readout.label} = ${Number(readout.value).toFixed(0)}${readout.unit}`
                : "\u00a0"}
            </Typography>
          </Box>
        </Stack>
      ) : null}

      <Box
        id={boardId}
        className="jxgbox"
        sx={{
          width: "100%",
          maxWidth: 640,
          // The "Correct solution" panel sits in an Alert whose message box shrinks to its text, so
          // width: 100% alone left the model board ~110px wide beside a short "Correct! ✓".
          minWidth: { xs: 260, sm: 420 },
          // A fixed pixel height: JSXGraph sizes its drawing surface from the container when the board
          // is created, and a CSS aspect-ratio box can still be 0px tall at that moment — the bottom of
          // the grid then never rendered and clicks landed half a centimetre off.
          height: height || { xs: 360, sm: 480 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "#fff",
          touchAction: "none",
        }}
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
      {renaming && !readOnly ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <TextField
                size="small"
                autoFocus
                label={`Rename ${renaming.item.name}`}
                value={renaming.text}
                error={!!renaming.error}
                helperText={renaming.error || " "}
                onChange={(e) => setRenaming({ ...renaming, text: e.target.value, error: null })}
                onKeyDown={(e) => { if (e.key === "Enter") commitRename(); }}
                inputProps={{ maxLength: 3, style: { width: 60, textTransform: "uppercase" } }}
              />
              <Button size="small" variant="contained" onClick={commitRename}>Save</Button>
              <Button size="small" onClick={() => setRenaming(null)}>Cancel</Button>
            </Stack>
          ) : null}

      {measureList.length > 0 ? (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {measureList.map((m) => {
            const unit = String(m.unit || "").trim();
            return (
              <TextField
                key={m.label}
                size="small"
                label={`${m.prompt || m.label}${unit ? ` (${unit})` : ""}`}
                value={measureValues[m.label] ?? ""}
                onChange={(e) => setMeasure(m.label, e.target.value)}
                disabled={readOnly}
                // Wide enough for the whole label — "Length of AB (cm)" was cut to "Length of …".
                sx={{ minWidth: 170 }}
                inputProps={{ inputMode: "decimal", style: { width: 90 } }}
              />
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
}
